import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import { getCustomer, getCustomerBills } from '../../../api/customer.api';
import { formatCurrency } from '../../../utils/formatters';
import { getSocket } from '../../../services/socket.service';
import styles from '../../../assets/scss/pages/CustomerHistory.module.scss';

const STATUS_MAP = {
    paid: { label: 'Đã thanh toán', class: 'paid' },
    pending: { label: 'Chưa thanh toán', class: 'pending' },
    partial: { label: 'TT một phần', class: 'partial' },
    cancelled: { label: 'Đã huỷ', class: 'cancelled' },
};

function formatDateTime(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatRelativeDate(dateStr) {
    if (!dateStr) return '';
    const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (diffDays === 0) return 'HÔM NAY';
    if (diffDays === 1) return 'HÔM QUA';
    return `${diffDays} NGÀY TRƯỚC`;
}

function formatBillCode(bill) {
    return bill.code || `HD${String(bill.id).padStart(6, '0')}`;
}

export default function CustomerBuyHistory() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [custRes, billsRes] = await Promise.all([
                getCustomer(id),
                getCustomerBills(id, { limit: 1000 }),
            ]);
            setCustomer(custRes.data.customer || custRes.data);
            const list = billsRes.data.data || billsRes.data.bills || billsRes.data || [];
            setBills(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error('Error fetching buy history:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Socket realtime
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;
        const refresh = () => {
            getCustomerBills(id, { limit: 1000 }).then(({ data }) => {
                const list = data.data || data.bills || data || [];
                setBills(Array.isArray(list) ? list : []);
            }).catch(() => {});
        };
        socket.on('bill.created', refresh);
        socket.on('bill.updated', refresh);
        socket.on('bill.deleted', refresh);
        return () => {
            socket.off('bill.created', refresh);
            socket.off('bill.updated', refresh);
            socket.off('bill.deleted', refresh);
        };
    }, [id]);

    const totalAmount = bills.filter((b) => b.status === 'paid').reduce((sum, b) => sum + (parseFloat(b.total) || 0), 0);

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <div>
                        <h1 className={styles.headerTitle}>Lịch sử mua hàng</h1>
                        <span className={styles.headerSub}>{customer?.name || 'Khách hàng'}</span>
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className={styles.summary}>
                <div className={styles.summaryItem}>
                    <ShoppingBag size={18} />
                    <span>{bills.length} giao dịch</span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryAmount}>{formatCurrency(totalAmount)}</span>
                </div>
            </div>

            {/* List */}
            <div className={styles.card}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} />
                        Đang tải...
                    </div>
                ) : bills.length === 0 ? (
                    <div className={styles.empty}>Chưa có hoá đơn nào</div>
                ) : (
                    <div className={styles.list}>
                        {bills.map((bill) => {
                            const statusInfo = STATUS_MAP[bill.status] || STATUS_MAP.paid;
                            const items = bill.details || bill.items || [];
                            return (
                                <div
                                    key={bill.id}
                                    className={styles.item}
                                    onClick={() => navigate(`/admin/bills/${bill.id}`)}
                                >
                                    <div className={styles.itemTop}>
                                        <div>
                                            <span className={styles.relDate}>{formatRelativeDate(bill.checkoutTime)}</span>
                                            <span className={styles.meta}>
                                                {formatDateTime(bill.checkoutTime)} &middot; {formatBillCode(bill)}
                                            </span>
                                        </div>
                                        <span className={`${styles.amount} ${bill.status !== 'paid' ? styles.amountUnpaid : ''}`}>
                                            {formatCurrency(bill.total)}
                                        </span>
                                    </div>
                                    <div className={styles.itemBottom}>
                                        <span className={styles.services}>
                                            {items.map((it) => {
                                                const name = it.service?.name || it.name || 'Dịch vụ';
                                                const qty = parseFloat(it.quantity) || 1;
                                                return qty > 1 ? `${name} x${qty}` : name;
                                            }).join(', ')}
                                        </span>
                                        <span className={`${styles.status} ${styles[`status${statusInfo.class.charAt(0).toUpperCase() + statusInfo.class.slice(1)}`]}`}>
                                            {statusInfo.label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
